import {cn} from "../../utils";
import styles from "./Button.module.css";

type ButtonBaseProps = {
    children?: React.ReactNode;
    icon?: React.ReactNode;
    iconPosition?: "left" | "right";
    subtext?: string;
    size?: "normal" | "small";
    variant?: "primary" | "primary-light" | "secondary" | "error" | "error-light";
    disabled?: boolean;
};

type ButtonAsButton = ButtonBaseProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children" | "formAction"> & {
        href?: undefined;
        type?: "button" | "submit" | "reset";
    };

type ButtonAsLink = ButtonBaseProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children"> & {
        href: string;
        type?: undefined;
    };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

export function Button(props: ButtonProps) {
    const {
        children,
        className,
        disabled,
        icon,
        iconPosition = "left",
        size = "normal",
        subtext,
        variant = "primary",
        ...restProps
    } = props;

    const isIconOnly = !children && !!icon;
    const isSmall = size === "small";

    if (isIconOnly && !restProps["aria-label"]) {
        console.warn("Icon-only button should have aria-label for accessibility");
    }

    const isLink = "href" in props && props.href !== undefined;

    const inner = (
        <>
            {!!icon && iconPosition === "left" && <span className={styles.icon}>{icon}</span>}
            {!!children && (
                <span className={styles.content}>
                    <span className={styles.text}>{children}</span>
                    {!!subtext && !isSmall && <span className={styles.subtext}>{subtext}</span>}
                </span>
            )}
            {!!icon && iconPosition === "right" && <span className={styles.icon}>{icon}</span>}
        </>
    );

    const classes = cn(
        styles.button,
        styles[variant satisfies ButtonProps["variant"]],
        isSmall && styles.small,
        disabled && styles.disabled,
        isIconOnly && styles["icon-only"],
        className
    );

    if (isLink) {
        const linkProps = restProps as Omit<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            "children"
        >;
        return (
            <a className={classes} {...linkProps}>
                {inner}
            </a>
        );
    }

    const buttonProps = restProps as Omit<
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        "children"
    >;
    return (
        <button type="button" className={classes} disabled={disabled} {...buttonProps}>
            {inner}
        </button>
    );
}
